import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import { getKpiPointRulesApi, updateKpiPointRulesApi } from '@/lib/reports-api';
import type {
  KpiOnlineRule,
  KpiPointRulesConfig,
  KpiQcRule,
  KpiSequenceRule,
} from '@/lib/types';
import {
  incrementsToText,
  parseIncrements,
  previewCumulativePoints,
} from '@/lib/kpi-point-rules-form';
import { Card } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/empty-state';

/**
 * Issue #272 — 管理员「系统设置 → KPI考核 → 积分规则」。
 *
 * 真实 GET/PATCH /system/kpi-point-rules（ADMIN+，按 orgId 隔离）。三条规则（在线时长 / 学习中心 /
 * 质检中心）均「基础分 + 累加增量序列（末值重复）+ 每日封顶」可配置，运行时即时生效——排行榜 /
 * 首页 / 个人中心本月积分按新规则推导。越界值由服务端归一化钳制。
 */
const INPUT =
  'w-24 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:bg-slate-50';
const TEXT_INPUT =
  'w-44 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:bg-slate-50';
const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';

/** 表单草稿：把增量序列改为可编辑文本，其余沿用配置数值。 */
interface RuleDraft {
  enabled: boolean;
  base: string;
  increments: string;
  dailyCap: string;
  blockHours?: string;
  scoreThreshold?: string;
  /** Issue #386：在线积分防挂机开关（仅在线时长规则使用）。 */
  requiresLearningOrQcPoints?: boolean;
}
interface Draft {
  onlineTime: RuleDraft;
  learning: RuleDraft;
  qc: RuleDraft;
}

function toDraft(c: KpiPointRulesConfig): Draft {
  return {
    onlineTime: {
      enabled: c.onlineTime.enabled,
      base: String(c.onlineTime.base),
      increments: incrementsToText(c.onlineTime.increments),
      dailyCap: String(c.onlineTime.dailyCap),
      blockHours: String(c.onlineTime.blockHours),
      requiresLearningOrQcPoints: c.onlineTime.requiresLearningOrQcPoints,
    },
    learning: {
      enabled: c.learning.enabled,
      base: String(c.learning.base),
      increments: incrementsToText(c.learning.increments),
      dailyCap: String(c.learning.dailyCap),
    },
    qc: {
      enabled: c.qc.enabled,
      base: String(c.qc.base),
      increments: incrementsToText(c.qc.increments),
      dailyCap: String(c.qc.dailyCap),
      scoreThreshold: String(c.qc.scoreThreshold),
    },
  };
}

function toSequenceRule(d: RuleDraft): KpiSequenceRule {
  return {
    enabled: d.enabled,
    base: Number(d.base) || 0,
    increments: parseIncrements(d.increments),
    dailyCap: Number(d.dailyCap) || 0,
  };
}

function buildConfig(draft: Draft): KpiPointRulesConfig {
  const onlineTime: KpiOnlineRule = {
    ...toSequenceRule(draft.onlineTime),
    blockHours: Number(draft.onlineTime.blockHours) || 4,
    // Issue #386：默认开启防挂机；未定义时回退 true（与后端出厂默认一致）。
    requiresLearningOrQcPoints: draft.onlineTime.requiresLearningOrQcPoints ?? true,
  };
  const qc: KpiQcRule = {
    ...toSequenceRule(draft.qc),
    scoreThreshold: Number(draft.qc.scoreThreshold) || 0,
  };
  return { onlineTime, learning: toSequenceRule(draft.learning), qc };
}

export function KpiPointRulesSettingsCard({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['system', 'kpi-point-rules'],
    queryFn: getKpiPointRulesApi,
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data?.config) setDraft(toDraft(query.data.config));
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (body: KpiPointRulesConfig) => updateKpiPointRulesApi(body),
    onSuccess: async (data) => {
      setSaveError(null);
      setSaved(true);
      setDraft(toDraft(data.config));
      await queryClient.invalidateQueries({ queryKey: ['system', 'kpi-point-rules'] });
    },
    onError: (err) =>
      setSaveError(
        err instanceof ApiError ? `${err.message}（HTTP ${err.status}）` : '保存失败，请重试。',
      ),
  });

  if (query.isLoading) {
    return (
      <Card title="积分规则">
        <LoadingState label="正在加载积分规则…" />
      </Card>
    );
  }
  if (query.isError || !draft) {
    return (
      <Card title="积分规则">
        <ErrorState
          title="加载积分规则失败"
          description={query.error instanceof ApiError ? query.error.message : '请稍后重试。'}
          retry={() => void query.refetch()}
        />
      </Card>
    );
  }

  const patchRule = (key: keyof Draft, field: keyof RuleDraft, value: string | boolean) => {
    setSaved(false);
    setDraft((prev) => (prev ? { ...prev, [key]: { ...prev[key], [field]: value } } : prev));
  };

  const onSave = () => {
    setSaved(false);
    setSaveError(null);
    mutation.mutate(buildConfig(draft));
  };

  return (
    <Card
      title="积分规则"
      description="配置在线时长 / 学习中心 / 质检中心的积分规则（基础分 + 累加增量序列 + 每日封顶）。保存后排行榜与本月积分即时按新规则推导，无需重启。"
    >
      <div className="space-y-5" data-testid="kpi-point-rules">
        <RuleBlock
          testid="kpi-rule-onlineTime"
          title="在线时长"
          eventHint="每满「在线时长(小时)」记 1 次事件"
          draft={draft.onlineTime}
          canEdit={canEdit}
          onChange={(f, v) => patchRule('onlineTime', f, v)}
          extra={
            <>
              <Field label="每满(小时)记 1 次" hint="默认 4">
                <input
                  className={INPUT}
                  type="number"
                  min={0.25}
                  step={0.25}
                  value={draft.onlineTime.blockHours ?? ''}
                  disabled={!canEdit}
                  onChange={(e) => patchRule('onlineTime', 'blockHours', e.target.value)}
                  data-testid="kpi-onlineTime-blockHours"
                />
              </Field>
              <label className="flex max-w-[18rem] flex-col gap-1 text-xs text-slate-600">
                <span>
                  防挂机
                  <span className="ml-1 text-slate-400">（默认开启）</span>
                </span>
                <span className="flex items-start gap-1.5">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={draft.onlineTime.requiresLearningOrQcPoints ?? true}
                    disabled={!canEdit}
                    onChange={(e) =>
                      patchRule('onlineTime', 'requiresLearningOrQcPoints', e.target.checked)
                    }
                    data-testid="kpi-onlineTime-requiresLearningOrQcPoints"
                  />
                  <span>当日无学习积分且无质检积分时，该日在线积分记 0（在线时长仍照常统计）。</span>
                </span>
              </label>
            </>
          }
        />
        <RuleBlock
          testid="kpi-rule-learning"
          title="学习中心"
          eventHint="每完成 1 门课程记 1 次事件"
          draft={draft.learning}
          canEdit={canEdit}
          onChange={(f, v) => patchRule('learning', f, v)}
        />
        <RuleBlock
          testid="kpi-rule-qc"
          title="质检中心"
          eventHint="每通 AI 评分 > 阈值的录音 / 本地上传记 1 次事件"
          draft={draft.qc}
          canEdit={canEdit}
          onChange={(f, v) => patchRule('qc', f, v)}
          extra={
            <Field label="AI 分阈值(>)" hint="默认 70">
              <input
                className={INPUT}
                type="number"
                min={0}
                max={100}
                value={draft.qc.scoreThreshold ?? ''}
                disabled={!canEdit}
                onChange={(e) => patchRule('qc', 'scoreThreshold', e.target.value)}
                data-testid="kpi-qc-scoreThreshold"
              />
            </Field>
          }
        />
      </div>

      {canEdit ? (
        <div className="mt-4 flex items-center gap-3">
          <button className={PRIMARY_BTN} onClick={onSave} disabled={mutation.isPending} data-testid="kpi-save">
            {mutation.isPending ? '保存中…' : '保存积分规则'}
          </button>
          {saved ? <span className="text-xs text-emerald-600">已保存，排行榜 / 本月积分即时生效。</span> : null}
        </div>
      ) : (
        <p className="mt-3 text-xs text-slate-500">仅 ADMIN 及以上角色可修改积分规则。</p>
      )}
      {saveError ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {saveError}
        </div>
      ) : null}
    </Card>
  );
}

function RuleBlock({
  title,
  eventHint,
  draft,
  canEdit,
  onChange,
  extra,
  testid,
}: {
  title: string;
  eventHint: string;
  draft: RuleDraft;
  canEdit: boolean;
  onChange: (field: keyof RuleDraft, value: string | boolean) => void;
  extra?: ReactNode;
  testid: string;
}) {
  const preview = previewCumulativePoints(
    {
      base: Number(draft.base) || 0,
      increments: parseIncrements(draft.increments),
      dailyCap: Number(draft.dailyCap) || 0,
    },
    5,
  );
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid={testid}>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-800">{title}</span>
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={draft.enabled}
            disabled={!canEdit}
            onChange={(e) => onChange('enabled', e.target.checked)}
            data-testid={`${testid}-enabled`}
          />
          启用
        </label>
      </div>
      <p className="mb-2 text-xs text-slate-400">{eventHint}</p>
      <div className="flex flex-wrap gap-3">
        <Field label="基础分" hint="第 1 次">
          <input
            className={INPUT}
            type="number"
            min={0}
            value={draft.base}
            disabled={!canEdit || !draft.enabled}
            onChange={(e) => onChange('base', e.target.value)}
            data-testid={`${testid}-base`}
          />
        </Field>
        <Field label="累加增量" hint="第 2,3,4… 次，逗号分隔">
          <input
            className={TEXT_INPUT}
            type="text"
            value={draft.increments}
            disabled={!canEdit || !draft.enabled}
            onChange={(e) => onChange('increments', e.target.value)}
            data-testid={`${testid}-increments`}
          />
        </Field>
        <Field label="每日封顶" hint="该规则每日上限">
          <input
            className={INPUT}
            type="number"
            min={0}
            value={draft.dailyCap}
            disabled={!canEdit || !draft.enabled}
            onChange={(e) => onChange('dailyCap', e.target.value)}
            data-testid={`${testid}-dailyCap`}
          />
        </Field>
        {extra}
      </div>
      <p className="mt-2 text-xs text-slate-400">
        预览（第 1-5 次累计）：{preview.join(' / ')}
      </p>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-slate-600">
      <span>
        {label}
        <span className="ml-1 text-slate-400">（{hint}）</span>
      </span>
      {children}
    </label>
  );
}
